import { useQuery } from "@tanstack/react-query";

import { apiFetchPayloadText } from "@/api/client";

/**
 * Largest structure the in-page viewer will fetch and parse, in bytes.
 *
 * Deliberately far below the server's download cap
 * (`FLOWSTACK_MAX_PAYLOAD_DOWNLOAD_BYTES`, 64 MiB by default): that number
 * bounds what the API process will *serve*, while this one bounds what a
 * browser tab will hold as text, hand to a parser, and turn into WebGL
 * geometry — three copies, none of which the download path pays for.
 *
 * 8 MiB clears the structures FlowStack actually catalogues by orders of
 * magnitude: the ligand-scale SDF/mol2/pdb files that `Molecule`,
 * `ConformerEnsemble` and `ProteinStructure` carry are kilobytes to low
 * megabytes. What it refuses is the trajectory-scale payload that would lock
 * the tab up rather than draw — and it refuses it with a message, which is the
 * point. Compiled in rather than configured because it is a property of the
 * client's memory and the renderer, not of a deployment; the server-side knob
 * stays the operational one.
 */
export const MAX_VIEWER_PAYLOAD_BYTES = 8 * 1024 * 1024;

export function payloadTextQueryKey(downloadUrl: string) {
  return ["artifacts", "payload-text", downloadUrl] as const;
}

/**
 * Read one artifact payload as text for in-page rendering.
 *
 * Disabled until `downloadUrl` is non-null, so a page can call it
 * unconditionally while the artifact detail is still loading or while the
 * artifact turns out to have no viewable payload at all.
 *
 * `staleTime: Infinity` because the payload is content-addressed: the URL
 * embeds the artifact's `content_hash`, so the bytes behind it cannot change.
 * Re-fetching one would be pure cost.
 */
export function useArtifactPayloadText(downloadUrl: string | null) {
  return useQuery({
    queryKey: payloadTextQueryKey(downloadUrl ?? ""),
    enabled: downloadUrl !== null && downloadUrl !== "",
    queryFn: () => apiFetchPayloadText(downloadUrl as string, MAX_VIEWER_PAYLOAD_BYTES),
    staleTime: Infinity,
    // A 413 or a 404 is a property of the artifact, not a transient fault;
    // retrying re-fetches bytes that will be refused identically.
    retry: false,
  });
}
